"use client";
import Image from "next/image";

import mainLogo from "@/assets/icons/sidebar/main-logo.svg";
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { IconMenu2, IconX } from "@tabler/icons-react";
import { Link, usePathname } from "@/i18n/navigation";
import { Separator } from "@/components/ui/separator";
import { useAuthModal } from "@/hooks/auth/useAuthModal";
import { useOpenIntegrationLink } from "@/hooks/integration-api/useIntegration";
import { useUser } from "@/hooks/user/useUser";
import { ACCOUNT_SETTINGS_ROUTES } from "@/utils/constants/accountSettingsRoutes";
import {
  mobileAccountMenuItems,
  SIDEBAR_ICONS,
  SIDEBAR_ITEMS,
} from "../layout.type";
import { useLogout } from "@/hooks/auth/useAuth";
import { Typography } from "@/components/Typography";
import GlobalSearch from "./global-search/GlobalSearch";
import { bindScrollShadow } from "@/lib/scroll";
import { cn, FALLBACK_FEATURE_IMAGE } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const APP_VERSION = process.env.NEXT_PUBLIC_APP_VERSION;
const isExternalLink = (href: string) => /^https?:\/\//i.test(href);
const isPathMatch = (pathname: string, targetPath?: string) =>
  Boolean(
    targetPath &&
    !isExternalLink(targetPath) &&
    (pathname === targetPath || pathname.startsWith(`${targetPath}/`)),
  );

const MobileHeader = ({
  gSearch,
  shadow = true,
}: {
  gSearch?: boolean;
  shadow?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pathname = usePathname();
  const { openLogin, openRegister } = useAuthModal();
  const user = useUser();
  const { mutateAsync: logout, isPending: isLogoutPending } = useLogout();
  const { openIntegrationLink, isOpeningIntegrationLink } =
    useOpenIntegrationLink();

  useEffect(() => {
    const el = containerRef.current;
    if (!shadow || !el) return;
    return bindScrollShadow(el, { threshold: 60 });
  }, [shadow]);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleLoginClick = () => {
    setOpen(false);
    void openLogin("email");
  };

  const handleRegisterClick = () => {
    setOpen(false);
    void openRegister();
  };

  const handleLogoutClick = async () => {
    setOpen(false);
    await logout();
  };

  const displayName = user.data
    ? `${user.data.firstName} ${user.data.lastName}`.trim()
    : "";
  const avatarSrc = user.data?.avatar || FALLBACK_FEATURE_IMAGE;

  return (
    <div
      ref={containerRef}
      className="top-0 z-[100] md:hidden transition-shadow"
    >
      <header className="relative flex h-[72px] items-center justify-between gap-2 bg-white px-4 backdrop-blur-sm">
        <Link href="/" className="flex items-center" aria-label="Home">
          <div className="flex size-11 items-center justify-center rounded-full">
            <Image
              src={mainLogo}
              alt="Tovtrip"
              width={48}
              height={48}
              className="h-[48px] w-[48px]"
            />
          </div>
        </Link>

        {gSearch ? (
          <div className="w-full">
            <GlobalSearch isScrolled={true} />
          </div>
        ) : null}

        <button
          type="button"
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((v) => !v)}
          className="flex size-11 transition icon-bg"
        >
          {open ? (
            <IconX className="size-6" stroke={2} />
          ) : (
            <IconMenu2 className="size-6" stroke={2} />
          )}
        </button>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
            className="fixed inset-x-0 bottom-0 top-[72px] z-[100] flex flex-col overflow-y-auto border-t bg-white"
          >
            {/* Auth Buttons */}
            <nav className="py-2 border-b px-6">
              {user.isLoading ? null : user.data ? (
                <div className="flex items-center gap-3">
                  <Image
                    src={avatarSrc}
                    alt="User avatar"
                    width={48}
                    height={48}
                    unoptimized={typeof avatarSrc === "string"}
                    className="size-12 rounded-full border"
                  />
                  <div className="min-w-0 flex-1">
                    <Typography variant="subtitle1" className="truncate">
                      Sour Sdei, {displayName}
                    </Typography>
                    <Link
                      href={ACCOUNT_SETTINGS_ROUTES.profile}
                      onClick={() => setOpen(false)}
                      className="hover:underline hover:text-secondary"
                    >
                      <Typography
                        variant="subtitle1"
                        className="truncate text-secondary"
                      >
                        View Profile
                      </Typography>
                    </Link>
                  </div>
                  <Button
                    onClick={() => void handleLogoutClick()}
                    disabled={isLogoutPending}
                    aria-label="Logout"
                    size={"lg"}
                    variant={"outline"}
                  >
                    Logout
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button
                    onClick={handleRegisterClick}
                    size={"lg"}
                    className="bg-white w-full flex-1"
                    variant={"outline"}
                  >
                    Register
                  </Button>

                  <Button
                    onClick={handleLoginClick}
                    size={"lg"}
                    className="bg-white w-full flex-1 bg-primary"
                  >
                    Login
                  </Button>
                </div>
              )}
            </nav>

            {/* Nav Items */}
            <nav className="flex flex-col overflow-y-auto px-2 tovtrip-scrollbar mt-2">
              {user.data ? (
                <>
                  {mobileAccountMenuItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = isPathMatch(pathname, item.href);

                    return (
                      <Link
                        key={item.label}
                        href={item.href}
                        onClick={() => setOpen(false)}
                      >
                        <Typography
                          variant="subtitle1"
                          className={cn(
                            "flex items-center gap-4 h-12 px-4 text-body hover:text-heading hover:bg-subtle rounded-full",
                            isActive && "bg-subtle rounded-full text-heading",
                          )}
                        >
                          <Icon
                            className={cn(
                              "size-6 shrink-0",
                              isActive && "text-heading",
                            )}
                            stroke={1.8}
                          />
                          {item.label}
                        </Typography>
                      </Link>
                    );
                  })}
                  <Separator className="my-2" />
                </>
              ) : null}

              {SIDEBAR_ITEMS.map((item) => {
                const Icon = SIDEBAR_ICONS[item.tablerIcon];
                const integrationKey = item.integrationKey;
                const isActive =
                  !item.disableAutoActive &&
                  (isPathMatch(pathname, item.href) ||
                    Boolean(
                      item.matchPaths?.some((p) => isPathMatch(pathname, p)),
                    ));

                const content = (
                  <Typography
                    variant="subtitle1"
                    className={cn(
                      "flex items-center gap-4 h-12 px-4 text-body hover:text-heading hover:bg-subtle rounded-full",
                      isActive && "bg-subtle rounded-full text-heading",
                    )}
                  >
                    <Icon
                      className={cn(
                        "size-6 shrink-0",
                        isActive && "text-heading",
                      )}
                      stroke={1.8}
                    />
                    {item.label}
                  </Typography>
                );

                return (
                  <div key={item.label}>
                    {integrationKey ? (
                      <button
                        type="button"
                        className="w-full"
                        disabled={isOpeningIntegrationLink(integrationKey)}
                        onClick={() => {
                          setOpen(false);
                          if (!isOpeningIntegrationLink(integrationKey)) {
                            void openIntegrationLink(integrationKey);
                          }
                        }}
                      >
                        {content}
                      </button>
                    ) : item.href ? (
                      <Link
                        href={item.href}
                        target={
                          isExternalLink(item.href) ? "_blank" : undefined
                        }
                        rel={
                          isExternalLink(item.href)
                            ? "noopener noreferrer"
                            : undefined
                        }
                        onClick={() => setOpen(false)}
                      >
                        {content}
                      </Link>
                    ) : (
                      <button
                        type="button"
                        className="w-full"
                        onClick={() => setOpen(false)}
                      >
                        {content}
                      </button>
                    )}
                    {item.showSeparatorAfter && <Separator className="my-2" />}
                  </div>
                );
              })}
            </nav>

            <footer className="mt-auto py-4">
              <Typography variant="body2" className="text-center text-body">
                Version {APP_VERSION}
              </Typography>
            </footer>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MobileHeader;
